import { Box, styled, Tooltip } from '@mui/material'

import useElementOverflow from 'ComponentLibrary/src/hooks/useElementOverflow'

import { Typography, TypographyProps } from 'ComponentLibrary/src/piazza/text/Typography/Typography'
import { ComponentProps, CSSProperties } from 'react'

type TooltipProps = Omit<ComponentProps<typeof Tooltip>, 'children' | 'title'>

type TextWithEllipsisProps = {
    children: React.ReactNode
    tooltipText: string
    placement?: ComponentProps<typeof Tooltip>['placement']
    tooltipProps?: TooltipProps
    typographyProps: Omit<TypographyProps, 'children'>
    textAlign?: CSSProperties['textAlign']
    fullWidth?: boolean
}

// This could be just a prop of the Typography component but it depends on how we want our component strategy to be
export function TextWithEllipsis({
    children,
    tooltipText,
    typographyProps,
    placement,
    tooltipProps,
    textAlign,
    fullWidth,
}: TextWithEllipsisProps) {
    const { isOverflowing, elementRef } = useElementOverflow({ verticalOverflow: !!typographyProps.lineClamp })
    return (
        <Tooltip title={isOverflowing ? tooltipText : ''} placement={placement} {...tooltipProps}>
            <Box width="100%" minWidth={0}>
                <Text {...typographyProps} ref={elementRef} $textAlign={textAlign} $fullWidth={fullWidth}>
                    {children}
                </Text>
            </Box>
        </Tooltip>
    )
}

const Text = styled(Typography)<{ lineClamp?: number; $textAlign?: CSSProperties['textAlign']; $fullWidth?: boolean }>(
    ({ lineClamp, $textAlign, $fullWidth }) => ({
        whiteSpace: !lineClamp ? 'nowrap' : '',
        lineClamp,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: $fullWidth ? '100%' : 'fit-content',
        width: $fullWidth ? '100%' : undefined,
        textAlign: $textAlign,
        // Tight value line-heights can clip descenders when ellipsis requires hidden overflow.
        paddingBottom: !lineClamp ? '1px' : undefined,
        marginBottom: !lineClamp ? '-1px' : undefined,
    })
)
